// @/config/sidebar-theme.js
// Single source of truth for all sidebar layout variant styles
// Edit this file to customize the appearance of each layout variant
//
// Variants:
// - "default": Light theme (current production look)
// - "widget": Alternative theme (customize as needed)

// ============================================================================
// LAYOUT VARIANTS
// ============================================================================

export const LAYOUT_DEFAULT = "default";
export const LAYOUT_WIDGET = "widget";

// ============================================================================
// THEME CONFIGURATION
// ============================================================================

export const sidebarTheme = {
  // ===========================================================================
  // DEFAULT VARIANT (Light)
  // ===========================================================================
  default: {
    // -------------------------------------------------------------------------
    // Main Container (LayoutMain in main.js)
    // -------------------------------------------------------------------------
    container: {
      // Outermost wrapper
      outer: "bg-zinc-50",
      // Inner flex wrapper with padding
      inner: "p-2",
      // Content card (motion.div)
      card: "rounded-2xl bg-white shadow-2xs ring-1 ring-zinc-200",
    },

    // -------------------------------------------------------------------------
    // Sidebar Container (LayoutSidebar in main.js)
    // -------------------------------------------------------------------------
    sidebar: "border-r border-zinc-200/50 bg-zinc-50",

    // -------------------------------------------------------------------------
    // Navigation Widget (navigation-widget.js)
    // -------------------------------------------------------------------------
    widget: {
      // Collapse/expand toggle button
      collapseButton:
        "bg-white shadow-lg ring-1 ring-zinc-100 text-zinc-400",
      // Logo dropdown button hover state
      logoButtonHover: "hover:bg-white hover:shadow-xs",
      // Collapsed state logo icon
      collapsedLogo: "bg-white text-black",
      // Header border below logo
      headerBorder: "border-zinc-200/50",
      // Footer border above user avatar
      footerBorder: "border-zinc-200/50",
      // User dropdown button hover state
      userButtonHover: "hover:bg-white hover:shadow-xs",
      // Chevron icon colors
      chevronColor: "text-zinc-600",
    },

    // -------------------------------------------------------------------------
    // Navigation List (nav-list.jsx)
    // -------------------------------------------------------------------------
    nav: {
      // Section heading (e.g., "Report", "Reduce")
      heading: "text-black/25",
      // Gap between nav items
      itemGap: "gap-1",
      itemGapCollapsed: "gap-2",
      // Navigation item states
      item: {
        base: "text-zinc-800",
        active: "bg-white shadow-xs font-semibold",
        // Separate hover states for expanded and collapsed modes
        hoverExpanded: "hover:bg-white hover:shadow-xs",
        hoverCollapsed: "hover:bg-white hover:shadow-xs",
      },
      // Divider line between sections
      divider: "bg-zinc-200/80",
      // Floating active indicator (navigation-widget.js)
      indicator: {
        // Enable/disable the floating indicator
        enabled: false,
        // Base styling for the indicator pill
        base: "w-2 h-7! rounded-full bg-black",
        // Position offset when expanded
        expandedLeft: "-left-5",
        // Height when expanded
        expandedHeight: "24px",
        // Vertical positions for each nav item (expanded)
        expandedPositions: {
          overview: "28px",
          selfAssessment: "76px",
          carbonAccounting: "124px",
          supplyChain: "196px",
        },
      },
    },

    // -------------------------------------------------------------------------
    // Avatar (avatar.jsx)
    // -------------------------------------------------------------------------
    avatar: {
      // Avatar circle container
      container: "bg-black",
      // Avatar initials text
      text: "text-white",
      // User name text (in AvatarWithInfo)
      name: "font-fono text-sm/tight truncate pr-3",
      // Company name text (in AvatarWithInfo)
      company: "font-fono text-zinc-500 tracking-tighter pr-3",
    },
  },

  // ===========================================================================
  // WIDGET VARIANT (Copy of Default - customize as needed)
  // ===========================================================================
  widget: {
    // -------------------------------------------------------------------------
    // Main Container (LayoutMain in main.js)
    // -------------------------------------------------------------------------
    container: {
      outer: "bg-white",
      inner: "p-0",
      card: "bg-white",
    },

    // -------------------------------------------------------------------------
    // Sidebar Container (LayoutSidebar in main.js)
    // -------------------------------------------------------------------------
    sidebar: "ring-1 ring-inset ring-black/5 bg-zinc-50 m-5 rounded-2xl",

    // -------------------------------------------------------------------------
    // Navigation Widget (navigation-widget.js)
    // -------------------------------------------------------------------------
    widget: {
      collapseButton:
        "bg-white shadow-lg ring-1 ring-black/5 text-zinc-400 hover:text-zinc-700",
      logoButtonHover: "hover:bg-white hover:shadow-xs",
      collapsedLogo: "bg-zinc-200 text-zinc-600",
      headerBorder: "border-zinc-200/60",
      footerBorder: "border-zinc-200/60",
      userButtonHover: "hover:bg-white hover:shadow-xs",
      chevronColor: "text-zinc-400",
    },

    // -------------------------------------------------------------------------
    // Navigation List (nav-list.jsx)
    // -------------------------------------------------------------------------
    nav: {
      heading: "text-black/25",
      itemGap: "gap-1",
      itemGapCollapsed: "gap-2",
      item: {
        base: "text-zinc-950",
        active: "bg-white shadow-xs",
        hoverExpanded: "hover:bg-white hover:shadow-xs",
        hoverCollapsed: "hover:bg-white hover:shadow-xs",
      },
      divider: "bg-zinc-200/80",
      indicator: {
        enabled: false,
        base: "w-1 rounded-full bg-primary",
        expandedLeft: "-left-1",
        expandedHeight: "24px",
        expandedPositions: {
          overview: "28px",
          selfAssessment: "76px",
          carbonAccounting: "124px",
          supplyChain: "196px",
        },
      },
    },

    // -------------------------------------------------------------------------
    // Avatar (avatar.jsx)
    // -------------------------------------------------------------------------
    avatar: {
      container: "bg-zinc-200/60",
      text: "text-zinc-600",
      name: "text-zinc-900",
      company: "text-zinc-500",
    },
  },
};

// ============================================================================
// HELPER FUNCTION
// ============================================================================

/**
 * Get theme styles for a given layout variant
 * @param {string} layout - "default" or "widget"
 * @returns {object} Theme configuration for the variant
 */
export function getTheme(layout) {
  return sidebarTheme[layout] || sidebarTheme[LAYOUT_DEFAULT];
}
